// LEÇONS MULTI-FICHIERS — assemblage de l'aperçu et navigation entre pages.
//
// À partir de la section 2 du parcours HTML/CSS, une leçon peut porter
// plusieurs fichiers (`data.files`) : c'est ce qui permet d'enseigner pourquoi
// `index.html` s'appelle ainsi, le fichier CSS à part, et les pages reliées.
// L'aperçu doit alors se comporter comme un navigateur devant un dossier :
// résoudre un `<link href="styles.css">` vers le contenu de l'onglet
// correspondant, et suivre un clic sur `<a href="menu.html">` en affichant
// l'autre page. Sans cette navigation, « deux fichiers » reste une abstraction ;
// avec elle, c'est un site.
//
// Ce module est du JavaScript PUR (aucune dépendance React) : il est utilisé
// par le player web ET rejoué tel quel par les harnais Node. Le portage mobile
// s'en servira comme référence.

/**
 * Nom de fichier canonique : minuscules, sans le `./` de tête qu'un apprenant
 * écrit naturellement dans un href. C'est la clé de TOUTES les comparaisons de
 * noms (onglets, résolution des link, navigation) : deux écritures du même nom
 * doivent toujours se retrouver.
 */
export function normalizeFileName(name) {
    return String(name == null ? "" : name)
        .trim()
        .replace(/^\.\//, "")
        .toLowerCase();
}

/**
 * Nom acceptable pour un fichier CRÉÉ ou RENOMMÉ par l'apprenant : les mêmes
 * règles que le lint impose aux auteurs (minuscules, pas d'espace, pas
 * d'accent, une extension connue). C'est aussi une leçon de nommage — l'unité 9
 * l'enseigne — donc l'éditeur ne laisse pas écrire ce qu'un serveur refuserait.
 * Vit ici pour que le web et l'app appliquent la MÊME règle : elle était
 * recopiée de chaque côté, et une divergence silencieuse a déjà coûté cher.
 */
export function isCreatableFileName(name) {
    return /^[a-z0-9][a-z0-9._-]*\.(html|css|js)$/.test(
        String(name == null ? "" : name).trim().toLowerCase()
    );
}

/**
 * RENOMMER un fichier dans la table de l'apprenant. Rend `null` si le
 * renommage est refusé (nom invalide, nom déjà pris, fichier protégé), sinon
 * la nouvelle table ET le nouvel ordre d'onglets, la position étant conservée.
 *
 * Ce qu'on ne fait PAS, volontairement : réécrire les liens qui pointaient vers
 * l'ancien nom. Un éditeur qui répare tout seul escamoterait ce que le parcours
 * enseigne — un lien cherche un nom exact, et il casse quand ce nom disparaît.
 * L'apprenant le découvre au clic, comme dans la vraie vie.
 *
 * `protege` : les fichiers fournis par l'exercice, qu'on ne renomme jamais (la
 * solution vise leur nom ; le renommer rendrait le verdict incompréhensible).
 */
export function renameFileInMap(files, order, oldName, rawName, protege) {
    const ancien = normalizeFileName(oldName);
    const neuf = normalizeFileName(rawName);
    if (protege && protege.has(ancien)) return null;
    if (!Object.prototype.hasOwnProperty.call(files, ancien)) return null;
    if (!isCreatableFileName(neuf)) return null;
    if (neuf === ancien) return { files, order, inchange: true };
    if (Object.prototype.hasOwnProperty.call(files, neuf)) return null;
    const suivant = {};
    for (const n of order) suivant[n === ancien ? neuf : n] = files[n];
    return {
        files: suivant,
        order: order.map((n) => (n === ancien ? neuf : n)),
        inchange: false,
    };
}

/**
 * Langue d'édition et de coloration d'un fichier, déduite de son extension.
 * L'éditeur est un VRAI éditeur : il reconnaît le type de ce qu'on lui donne,
 * y compris les extensions qui arriveront avec les parcours suivants.
 */
export function fileLanguage(name) {
    const n = normalizeFileName(name);
    if (n.endsWith(".css")) return "css";
    if (n.endsWith(".js")) return "javascript";
    if (n.endsWith(".py")) return "python";
    return "html";
}

/**
 * `data.files` (tableau [{name, content}]) -> Map ordonnée nom canonique ->
 * contenu. Tout ce qui suit (onglets, aperçu, correction) travaille sur cette
 * forme ; l'ordre du tableau est l'ordre des onglets.
 */
export function fileMapOf(files) {
    const map = new Map();
    for (const f of Array.isArray(files) ? files : []) {
        if (!f || typeof f.name !== "string") continue;
        map.set(normalizeFileName(f.name), String(f.content == null ? "" : f.content));
    }
    return map;
}

/**
 * La page d'ACCUEIL d'un jeu de fichiers : `index.html` si présent, sinon la
 * première page HTML. C'est la définition même de ce que la section 2
 * enseigne : le fichier que le serveur sert quand on ne demande rien.
 * Miroir EXACT de RepsGrader::entryFileName (PHP) et du grader mobile.
 */
export function entryFileName(names) {
    const list = Array.from(names || []);
    if (list.includes("index.html")) return "index.html";
    for (const n of list) {
        if (n.endsWith(".html")) return n;
    }
    return list.length ? list[0] : null;
}

/** Les pages navigables (fichiers .html), dans l'ordre des onglets. */
export function htmlPages(fileMap) {
    return Array.from(fileMap.keys()).filter((n) => n.endsWith(".html"));
}

/**
 * Assemble UNE page pour l'aperçu : son HTML, avec chaque `<link>` dont le
 * `href` désigne un fichier CSS connu remplacé par le contenu de ce fichier
 * dans une balise `<style>`. Un `<link>` vers un fichier inconnu est laissé
 * tel quel : il ne charge rien, exactement comme dans un vrai navigateur
 * devant un chemin cassé — c'est un comportement qu'on veut MONTRER, pas
 * masquer.
 */
export function assemblePage(fileMap, pageName) {
    const page = fileMap.get(normalizeFileName(pageName));
    if (page == null) return "";
    return page.replace(/<link\b[^>]*>/gi, (tag) => {
        // rel="stylesheet" requis, comme dans un vrai navigateur : un <link>
        // sans rel (ou rel="icon") ne charge PAS la feuille. L'oubli du rel est
        // exactement le genre d'erreur que la leçon doit laisser VOIR — un
        // aperçu qui stylait quand même aurait menti, puis contredit le verdict.
        if (!/\brel\s*=\s*(?:"stylesheet"|'stylesheet'|stylesheet(?=[\s>]))/i.test(tag)) {
            return tag;
        }
        const href = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(tag);
        const target = href
            ? normalizeFileName(href[1] != null ? href[1] : href[2] != null ? href[2] : href[3])
            : "";
        if (target && target.endsWith(".css") && fileMap.has(target)) {
            return `<style>\n${fileMap.get(target)}\n</style>`;
        }
        return tag;
    });
}

/**
 * Cible de navigation d'un clic dans l'aperçu : le nom canonique de la page si
 * le `href` désigne une page connue, null sinon (ancre, lien externe, chemin
 * cassé). Le player décide alors : suivre, ou ne rien faire.
 */
export function navigationTarget(fileMap, href) {
    const raw = String(href == null ? "" : href);
    if (!raw || raw.startsWith("#")) return null;
    if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return null; // http:, mailto:, etc.
    const target = normalizeFileName(raw.split(/[?#]/)[0]);
    return target.endsWith(".html") && fileMap.has(target) ? target : null;
}

const ENTITES = {
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&quot;": '"',
    "&#39;": "'",
    "&apos;": "'",
    "&nbsp;": " ",
};

/**
 * Titre du document, celui que le navigateur écrit dans son onglet.
 *
 * C'est ce qui rend la balise `<title>` ENSEIGNABLE : sans onglet à l'écran,
 * elle est la seule balise du parcours dont l'effet est invisible. Retourne
 * null tant qu'il n'y a pas de `<title>` non vide — l'aperçu affiche alors le
 * nom du fichier, exactement comme un vrai navigateur devant une page sans
 * titre, et l'apprenant voit l'onglet changer au moment où il pose la balise.
 */
export function documentTitle(html) {
    const m = /<title[^>]*>([\s\S]*?)<\/title>/i.exec(
        String(html == null ? "" : html)
    );
    if (!m) return null;
    const texte = m[1]
        .replace(/&[a-z]+;|&#39;/gi, (e) => {
            const k = e.toLowerCase();
            return Object.prototype.hasOwnProperty.call(ENTITES, k)
                ? ENTITES[k]
                : e;
        })
        .replace(/\s+/g, " ")
        .trim();
    return texte || null;
}
